import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronUp, Loader2, RefreshCw, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const COLLAPSED_COUNT = 6;
const RING_SIZE = 52;
const RING_STROKE = 4;

const COMPANY_BADGE_PALETTE = [
  { light: "bg-violet-100 text-violet-800 border-violet-300", dark: "bg-violet-950/60 text-violet-300 border-violet-500/45" },
  { light: "bg-blue-100 text-blue-800 border-blue-300", dark: "bg-blue-950/60 text-blue-300 border-blue-500/45" },
  { light: "bg-emerald-100 text-emerald-800 border-emerald-300", dark: "bg-emerald-950/60 text-emerald-300 border-emerald-500/45" },
  { light: "bg-rose-100 text-rose-800 border-rose-300", dark: "bg-rose-950/60 text-rose-300 border-rose-500/45" },
  { light: "bg-amber-100 text-amber-900 border-amber-300", dark: "bg-amber-950/60 text-amber-300 border-amber-500/45" },
  { light: "bg-cyan-100 text-cyan-800 border-cyan-300", dark: "bg-cyan-950/60 text-cyan-300 border-cyan-500/45" },
  { light: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300", dark: "bg-fuchsia-950/60 text-fuchsia-300 border-fuchsia-500/45" },
  { light: "bg-orange-100 text-orange-800 border-orange-300", dark: "bg-orange-950/60 text-orange-300 border-orange-500/45" },
  { light: "bg-indigo-100 text-indigo-800 border-indigo-300", dark: "bg-indigo-950/60 text-indigo-300 border-indigo-500/45" },
  { light: "bg-teal-100 text-teal-800 border-teal-300", dark: "bg-teal-950/60 text-teal-300 border-teal-500/45" },
  { light: "bg-pink-100 text-pink-800 border-pink-300", dark: "bg-pink-950/60 text-pink-300 border-pink-500/45" },
  { light: "bg-lime-100 text-lime-800 border-lime-300", dark: "bg-lime-950/60 text-lime-300 border-lime-500/45" },
];

const getCompanyKey = (device) => {
  if (device?.company_id) return `id:${device.company_id}`;
  if (device?.company_name) return `name:${device.company_name}`;
  return "none";
};

const buildCompanyBadgeMap = (devices) => {
  const keys = [...new Set(devices.map(getCompanyKey))].sort();
  const map = {};
  keys.forEach((key, idx) => {
    map[key] = COMPANY_BADGE_PALETTE[idx % COMPANY_BADGE_PALETTE.length];
  });
  return map;
};

const getCompanyBadgeClass = (device, badgeMap, isDark) => {
  const palette = badgeMap[getCompanyKey(device)];
  if (!palette) {
    return isDark ? "bg-slate-800 text-slate-400 border-slate-600" : "bg-gray-100 text-gray-600 border-gray-300";
  }
  return isDark ? palette.dark : palette.light;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString("tr-TR");
};

const formatCompact = (n) => {
  const num = Number(n) || 0;
  if (num >= 10000) return `${(num / 1000).toFixed(1)}k`;
  return num.toLocaleString("tr-TR");
};

export const getDeviceFailedBadgeClass = (severity, isDark) => {
  if (severity === "critical") {
    return isDark
      ? "bg-red-950/40 text-red-300 border-red-500/25"
      : "bg-red-50 text-red-700 border-red-200";
  }
  if (severity === "warning") {
    return isDark
      ? "bg-amber-950/30 text-amber-300 border-amber-500/20"
      : "bg-amber-50 text-amber-800 border-amber-200";
  }
  return isDark
    ? "bg-slate-800 text-slate-500 border-slate-700"
    : "bg-gray-100 text-gray-500 border-gray-200";
};

export const DeviceFailedStatsBadge = ({ stat, isDark, compact = false }) => {
  if (!stat || !stat.terk_adet) {
    return <span className={`text-xs ${isDark ? "text-slate-600" : "text-gray-400"}`}>-</span>;
  }

  return (
    <Badge
      variant="outline"
      className={`font-mono tabular-nums ${compact ? "text-[10px] px-1.5 py-0" : "text-xs"} ${getDeviceFailedBadgeClass(stat.severity, isDark)}`}
      title={`${stat.terk_adet} eksik ses kaydı${stat.son_terk_tarihi ? ` · Son: ${formatDateTime(stat.son_terk_tarihi)}` : ""}`}
    >
      {stat.terk_adet.toLocaleString("tr-TR")}
    </Badge>
  );
};

const DeviceRing = ({ value, maxValue, isCritical, isDark }) => {
  const pct = Math.min(100, Math.max(4, (value / Math.max(maxValue, 1)) * 100));
  const radius = (RING_SIZE - RING_STROKE) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;
  const track = isDark ? "#1e293b" : "#f1f5f9";
  const stroke = isCritical
    ? (isDark ? "#f87171" : "#ef4444")
    : (isDark ? "#fbbf24" : "#f59e0b");

  return (
    <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
      <svg width={RING_SIZE} height={RING_SIZE} className="-rotate-90">
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill="none"
          stroke={track}
          strokeWidth={RING_STROKE}
        />
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={radius}
          fill="none"
          stroke={stroke}
          strokeWidth={RING_STROKE}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-[stroke-dashoffset] duration-500 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`text-[10px] font-bold font-mono tabular-nums leading-none ${isCritical ? (isDark ? "text-red-300" : "text-red-600") : (isDark ? "text-amber-300" : "text-amber-700")}`}>
          {formatCompact(value)}
        </span>
      </div>
    </div>
  );
};

const DeviceRingCard = ({ device, maxValue, isDark, labelClass, companyBadgeClass, onClick }) => {
  const isCritical = device.severity === "critical";
  const modelLabel = device.device_model || device.device_name || device.device_code || "—";
  const tooltip = [device.agent_name, modelLabel, device.company_name].filter(Boolean).join(" · ");

  return (
    <button
      type="button"
      onClick={() => onClick?.(device.device_id)}
      className={`group flex flex-col items-center gap-1 p-2 rounded-xl transition-all min-w-[80px] max-w-[96px] ${
        isDark
          ? "hover:bg-slate-800/60"
          : "hover:bg-gray-50"
      } ${onClick ? "cursor-pointer" : "cursor-default"}`}
      title={onClick ? `${tooltip} · Çağrı kayıtları` : tooltip}
    >
      <DeviceRing value={device.terk_adet} maxValue={maxValue} isCritical={isCritical} isDark={isDark} />
      <span className={`font-mono text-[10px] font-semibold ${isDark ? "text-cyan-400 group-hover:underline" : "text-cyan-600 group-hover:underline"}`}>
        {device.device_id}
      </span>
      {device.agent_name ? (
        <span className={`text-[9px] font-medium leading-tight text-center line-clamp-1 w-full ${isDark ? "text-emerald-400/90" : "text-emerald-700"}`}>
          {device.agent_name}
        </span>
      ) : (
        <span className={`text-[9px] leading-tight ${labelClass}`}>—</span>
      )}
      <span className={`text-[9px] leading-tight text-center line-clamp-1 w-full ${labelClass}`}>
        {modelLabel}
      </span>
      {device.company_name && (
        <Badge
          variant="outline"
          className={`text-[8px] px-1.5 py-0 h-4 max-w-full font-semibold leading-none border truncate ${companyBadgeClass}`}
        >
          {device.company_name}
        </Badge>
      )}
    </button>
  );
};

const DeviceFailedStatsPanel = ({
  API,
  isDark,
  textClass,
  labelClass,
  cardClass,
  isSuperAdmin,
  companyId,
  onStatsMapChange,
  onDeviceClick,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (isSuperAdmin && companyId && companyId !== "all") {
        params.append("company_id", companyId);
      }
      const res = await axios.get(`${API}/device-failed-stats?${params}`, { timeout: 60000 });
      setData(res.data);
      onStatsMapChange?.(res.data?.map || {});
    } catch (error) {
      console.error("Device failed stats error:", error);
      setData(null);
      onStatsMapChange?.({});
    } finally {
      setLoading(false);
    }
  }, [API, isSuperAdmin, companyId, onStatsMapChange]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    setShowAll(false);
  }, [companyId]);

  const summary = data?.summary;
  const deviceList = useMemo(() => data?.devices || [], [data?.devices]);
  const companyBadgeMap = useMemo(() => buildCompanyBadgeMap(deviceList), [deviceList]);
  const maxTerk = useMemo(() => Math.max(...deviceList.map((d) => d.terk_adet), 1), [deviceList]);

  const visibleDevices = showAll ? deviceList : deviceList.slice(0, COLLAPSED_COUNT);
  const hasMore = deviceList.length > COLLAPSED_COUNT;

  const companyLegend = useMemo(() => {
    const seen = new Map();
    deviceList.forEach((device) => {
      const key = getCompanyKey(device);
      if (!device.company_name || seen.has(key)) return;
      seen.set(key, {
        name: device.company_name,
        className: getCompanyBadgeClass(device, companyBadgeMap, isDark),
      });
    });
    return [...seen.values()];
  }, [deviceList, companyBadgeMap, isDark]);

  if (loading && !data) {
    return (
      <Card className={`${cardClass} overflow-hidden`}>
        <CardContent className="py-5 flex items-center justify-center gap-2">
          <Loader2 className={`w-4 h-4 animate-spin ${isDark ? "text-slate-400" : "text-gray-400"}`} />
          <span className={`text-xs ${labelClass}`}>Eksik ses kayıtları yükleniyor…</span>
        </CardContent>
      </Card>
    );
  }

  if (!deviceList.length) {
    return (
      <Card className={`${cardClass} overflow-hidden`}>
        <CardContent className="py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <VolumeX className={`w-4 h-4 shrink-0 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
            <span className={`text-xs ${labelClass}`}>Eksik ses kaydı olan cihaz yok</span>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchStats} disabled={loading} className="h-7 w-7 p-0 shrink-0">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${cardClass} overflow-hidden`}>
      <CardContent className="py-3 px-4">
        {/* Header — tek satır özet */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 min-w-0 flex-wrap">
            <VolumeX className={`w-4 h-4 shrink-0 ${isDark ? "text-slate-400" : "text-gray-500"}`} />
            <span className={`text-xs font-medium ${textClass}`}>Eksik Ses</span>
            {summary && (
              <>
                <span className={`text-[10px] ${labelClass}`}>·</span>
                <span className={`text-[11px] font-mono tabular-nums ${isDark ? "text-red-300/90" : "text-red-600"}`}>
                  {(summary.total_failed_records || 0).toLocaleString("tr-TR")} kayıt
                </span>
                <span className={`text-[10px] ${labelClass}`}>·</span>
                <span className={`text-[11px] ${labelClass}`}>{summary.total_devices} cihaz</span>
                {(summary.blocked_devices || 0) > 0 && (
                  <>
                    <span className={`text-[10px] ${labelClass}`}>·</span>
                    <span className={`text-[11px] ${isDark ? "text-red-400" : "text-red-500"}`}>
                      {summary.blocked_devices} kritik
                    </span>
                  </>
                )}
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchStats}
            disabled={loading}
            className="h-7 w-7 p-0 shrink-0"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {companyLegend.length > 1 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {companyLegend.map((item) => (
              <Badge
                key={item.name}
                variant="outline"
                className={`text-[9px] px-2 py-0 h-5 font-semibold border ${item.className}`}
              >
                {item.name}
              </Badge>
            ))}
          </div>
        )}

        {/* Yuvarlak bar grid */}
        <div
          className={`flex flex-wrap gap-1 justify-start ${
            showAll ? "max-h-[220px] overflow-y-auto pr-1 scrollbar-thin" : ""
          }`}
        >
          {visibleDevices.map((device) => (
            <DeviceRingCard
              key={device.device_id}
              device={device}
              maxValue={maxTerk}
              isDark={isDark}
              labelClass={labelClass}
              companyBadgeClass={getCompanyBadgeClass(device, companyBadgeMap, isDark)}
              onClick={onDeviceClick}
            />
          ))}
        </div>

        {/* Tümünü göster / daralt */}
        {hasMore && (
          <div className="mt-2 pt-2 border-t border-dashed flex justify-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAll((v) => !v)}
              className={`h-7 text-[11px] gap-1 ${isDark ? "text-slate-400 hover:text-slate-200" : "text-gray-500 hover:text-gray-800"}`}
            >
              {showAll ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  Daralt
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  Tümünü göster ({deviceList.length})
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceFailedStatsPanel;
